
import { useEffect, useRef, useState } from "react";
import { SpinnerText } from "../../ui/Spinner";
import {
  getEuipoTrademarkDetails,
  searchEuipoTrademarks,
  type EuipoTrademarkResult,
} from "../../../services/euipoTrademarkService";

interface ImportClaimFormProps {
  onCancel: () => void;
  onSubmit: (data: {
    office: string;
    filingNumber: string;
    filingDate?: string;
    registrationDate?: string;
    registrationNumber?: string;
    markName?: string;
  }) => void;
  labelCountry: string;
  labelTrademark: string;
  submitText: string;
  type: "priority" | "seniority";
  referenceDate?: string;
}

type ImportData = {
  office: string;
  filingNumber: string;
  filingDate?: string;
  registrationDate?: string;
  registrationNumber?: string;
  markName?: string;
};

type ManualData = {
  markName: string;
  filingNumber: string;
  filingDate: string;
  registrationNumber: string;
  registrationDate: string;
};

const COUNTRIES: [string, string][] = [
  ["AT", "Austria"],
  ["BE", "Belgium"],
  ["BG", "Bulgaria"],
  ["CY", "Cyprus"],
  ["CZ", "Czechia"],
  ["DE", "Germany"],
  ["DK", "Denmark"],
  ["EE", "Estonia"],
  ["ES", "Spain"],
  ["FI", "Finland"],
  ["FR", "France"],
  ["GR", "Greece"],
  ["HR", "Croatia"],
  ["HU", "Hungary"],
  ["IE", "Ireland"],
  ["IT", "Italy"],
  ["LT", "Lithuania"],
  ["LU", "Luxembourg"],
  ["LV", "Latvia"],
  ["MT", "Malta"],
  ["NL", "Netherlands"],
  ["PL", "Poland"],
  ["PT", "Portugal"],
  ["RO", "Romania"],
  ["SE", "Sweden"],
  ["SI", "Slovenia"],
  ["SK", "Slovakia"],
  ["GB", "United Kingdom (historical)"],
];

function withinSixMonths(
  dateString: string,
  referenceString: string
): boolean {
  const filing = new Date(`${dateString}T12:00:00Z`);
  const reference = new Date(`${referenceString}T12:00:00Z`);

  if (
    Number.isNaN(filing.getTime()) ||
    Number.isNaN(reference.getTime()) ||
    filing > reference
  ) {
    return false;
  }

  const earliest = new Date(reference);
  const originalDay = earliest.getUTCDate();

  earliest.setUTCDate(1);
  earliest.setUTCMonth(earliest.getUTCMonth() - 6);

  const lastDay = new Date(
    Date.UTC(
      earliest.getUTCFullYear(),
      earliest.getUTCMonth() + 1,
      0
    )
  ).getUTCDate();

  earliest.setUTCDate(Math.min(originalDay, lastDay));

  return filing >= earliest;
}

function errorText(_error: unknown): string {
  return "Unable to load trademarks right now. Please try again.";
}

export default function ImportClaimForm({
  onCancel,
  onSubmit,
  labelCountry,
  labelTrademark,
  submitText,
  type,
  referenceDate,
}: ImportClaimFormProps) {
  const [searchText, setSearchText] = useState("");
  const [manualData, setManualData] = useState<ManualData>({
    markName: "",
    filingNumber: "",
    filingDate: "",
    registrationNumber: "",
    registrationDate: "",
  });

  const [selectedCountry, setSelectedCountry] = useState("");
  const [manualError, setManualError] = useState("");

  const [results, setResults] = useState<
    EuipoTrademarkResult[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [selected, setSelected] = useState<ImportData | null>(
    null
  );

  const requestId = useRef(0);

  const today = new Date().toISOString().slice(0, 10);
  const effectiveReferenceDate = referenceDate || today;

  const isSeniority = type === "seniority";

  useEffect(() => {
    if (isSeniority) {
      return;
    }

    const value = searchText.trim();
    const id = ++requestId.current;

    setResults([]);
    setSelected(null);
    setError("");
    setNotice("");
    setLoading(false);

    if (value.length < 2) {
      return;
    }

    if (/^DE\d/i.test(value)) {
      setNotice(
        "This search supports EUIPO application numbers and trademark names, but not national application numbers."
      );
      return;
    }

    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const found = await searchEuipoTrademarks({
          trademark: value,
          size: 10,
          claimType: "priority",
          referenceDate: effectiveReferenceDate,
        });

        if (id !== requestId.current) {
          return;
        }

        const eligible = Array.isArray(found)
          ? found.filter(
              (item) =>
                item.applicationDate &&
                withinSixMonths(
                  item.applicationDate,
                  effectiveReferenceDate
                )
            )
          : [];

        setResults(eligible);

        if (eligible.length === 0) {
          setNotice(
            "No EUIPO applications were found within the six-month priority period."
          );
        }
      } catch (e) {
        if (id === requestId.current) {
          setError(errorText(e));
        }
      } finally {
        if (id === requestId.current) {
          setLoading(false);
        }
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchText, effectiveReferenceDate, isSeniority]);

  const selectResult = async (
    item: EuipoTrademarkResult
  ) => {
    const id = ++requestId.current;

    setResults([]);
    setLoading(false);
    setLoadingDetails(true);
    setSelected(null);
    setError("");
    setNotice("");

    try {
      const details = await getEuipoTrademarkDetails(
        item.applicationNumber
      );

      if (id !== requestId.current) {
        return;
      }

      if (!details.applicationDate) {
        setError(
          "No filing date is available for this application."
        );
        return;
      }

      if (
        !withinSixMonths(
          details.applicationDate,
          effectiveReferenceDate
        )
      ) {
        setError(
          "The filing date is outside the six-month priority period or later than the reference filing date."
        );
        return;
      }

      setSelected({
        office: "EM",
        filingNumber:
          details.applicationNumber ||
          item.applicationNumber,
        filingDate: details.applicationDate,
        markName: item.markName,
      });
    } catch (e) {
      if (id === requestId.current) {
        setError(errorText(e));
      }
    } finally {
      if (id === requestId.current) {
        setLoadingDetails(false);
      }
    }
  };

  const updateManual = (
    field: keyof ManualData,
    value: string
  ) => {
    setManualData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setManualError("");
  };

  const submitManual = () => {
    if (!selectedCountry) {
      setManualError("Please select a country.");
      return;
    }

    if (
      !manualData.filingNumber.trim() ||
      !manualData.registrationNumber.trim() ||
      !manualData.registrationDate
    ) {
      setManualError(
        "Application number, registration number and registration date are required."
      );
      return;
    }

    if (
      manualData.filingDate &&
      manualData.filingDate > manualData.registrationDate
    ) {
      setManualError(
        "The filing date cannot be later than the registration date."
      );
      return;
    }

    onSubmit({
      office: selectedCountry,
      filingNumber: manualData.filingNumber.trim(),
      filingDate: manualData.filingDate || undefined,
      registrationNumber:
        manualData.registrationNumber.trim(),
      registrationDate: manualData.registrationDate,
      markName: manualData.markName.trim() || undefined,
    });
  };

  return (
    <div className="space-y-5 rounded-2xl border border-blue-200 bg-white p-5 font-sans shadow-sm">
      <h3 className="font-heading text-lg font-semibold text-dantzBlue">
        {isSeniority
          ? "Create new seniority"
          : "Import from existing EUIPO application"}
      </h3>

      {!isSeniority && (
        <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-700">
          The earlier application must have been filed
          within the preceding six months.
        </div>
      )}

      {isSeniority ? (
        <>
          <div className="space-y-2">
            <label
              htmlFor="seniority-country"
              className="block text-sm font-semibold text-dantzBlue"
            >
              Country *
            </label>

            <select
              id="seniority-country"
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                setManualError("");
              }}
              className="w-full rounded-lg border border-blue-200 bg-white px-3 py-3 text-sm text-dantzBlue focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Select country</option>

              {COUNTRIES.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            {(
              [
                [
                  "markName",
                  "Trademark name",
                  "text",
                ],
                [
                  "filingNumber",
                  "Application number",
                  "text",
                ],
                [
                  "filingDate",
                  "Filing date",
                  "date",
                ],
                [
                  "registrationNumber",
                  "Registration number",
                  "text",
                ],
                [
                  "registrationDate",
                  "Registration date",
                  "date",
                ],
              ] as const
            ).map(([field, label, inputType]) => {
              const required =
                field === "filingNumber" ||
                field === "registrationNumber" ||
                field === "registrationDate";

              return (
                <div key={field} className="space-y-2">
                  <label
                    htmlFor={`seniority-${field}`}
                    className="block text-sm font-semibold text-dantzBlue"
                  >
                    {label}
                    {required ? " *" : ""}
                  </label>

                  <input
                    id={`seniority-${field}`}
                    type={inputType}
                    value={manualData[field]}
                    onChange={(e) =>
                      updateManual(
                        field,
                        e.target.value
                      )
                    }
                    className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2.5 text-sm text-dantzBlue focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              );
            })}

            {manualError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {manualError}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <label
              htmlFor="euipo-search-text"
              className="block text-sm font-semibold text-dantzBlue"
            >
              {labelTrademark}
            </label>

            <input
              id="euipo-search-text"
              type="text"
              autoComplete="off"
              value={searchText}
              onChange={(e) =>
                setSearchText(e.target.value)
              }
              placeholder="Trademark name or EUIPO application number"
              className="w-full rounded-lg border border-blue-200 bg-white px-3 py-3 text-sm text-dantzBlue placeholder:text-blue-300 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          {loading && (
            <p
              className="text-sm text-blue-600"
              role="status"
            >
              <SpinnerText>Searching trademarks...</SpinnerText>
            </p>
          )}

          {loadingDetails && (
            <p
              className="text-sm text-blue-600"
              role="status"
            >
              <SpinnerText>Loading trademark details...</SpinnerText>
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {notice && (
            <div
              role="status"
              className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700"
            >
              {notice}
            </div>
          )}

          {results.length > 0 && (
            <div
              className="max-h-72 space-y-2 overflow-y-auto"
              aria-label="Trademark search results"
            >
              {results.map((item) => (
                <button
                  type="button"
                  key={item.applicationNumber}
                  onClick={() =>
                    void selectResult(item)
                  }
                  className="w-full rounded-xl border border-blue-200 bg-white p-3 text-left transition hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-semibold text-dantzBlue">
                      {item.markName ||
                        "Trademark without a word element"}
                    </span>

                    <span className="shrink-0 rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700">
                      <strong>Status:</strong>{" "}
                      {item.status ||
                        "Not available"}
                    </span>
                  </div>

                  <div className="mt-2 space-y-1 text-xs text-blue-600">
                    <p>
                      <strong>
                        Application number:
                      </strong>{" "}
                      {item.applicationNumber}
                    </p>

                    {item.applicationDate && (
                      <p>
                        <strong>
                          Filing date:
                        </strong>{" "}
                        {item.applicationDate}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className="space-y-1 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm">
              <p className="font-semibold text-dantzBlue">
                Selected trademark
              </p>

              {selected.markName && (
                <p className="text-blue-700">
                  <strong>Trademark:</strong>{" "}
                  {selected.markName}
                </p>
              )}

              <p className="text-blue-700">
                <strong>
                  {labelCountry}:
                </strong>{" "}
                {selected.office}
              </p>

              <p className="text-blue-700">
                <strong>
                  Application number:
                </strong>{" "}
                {selected.filingNumber}
              </p>

              {selected.filingDate && (
                <p className="text-blue-700">
                  <strong>
                    Filing date:
                  </strong>{" "}
                  {selected.filingDate}
                </p>
              )}
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap justify-end gap-3 border-t border-blue-100 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-blue-200 px-4 py-2.5 text-sm font-semibold text-dantzBlue hover:bg-blue-50"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={
            !isSeniority &&
            (!selected || loadingDetails)
          }
          onClick={() => {
            if (isSeniority) {
              submitManual();
            } else if (selected) {
              onSubmit(selected);
            }
          }}
          className="rounded-lg bg-dantzBlue px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitText}
        </button>
      </div>
    </div>
  );
}
